import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import { Tooltip, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import { axios, apiRequestOptions } from 'utils';
import { apiPath } from 'constants/apiPathConstants';

const useStyles = makeStyles(theme => ({
    root: {
        fontSize: 11,
        lineHeight: 1.4,
        color: theme.palette.text.secondary,
        wordBreak: 'break-all',
    },
    mismatch: {
        color: theme.palette.error.main,
        fontWeight: 600,
    },
}));

/** The UI's own release — baked in by `make version-stamp` before the build. */
export const UI_VERSION = process.env.REACT_APP_VERSION || null;
export const UI_BUILD_DATE = process.env.REACT_APP_BUILD_DATE || null;

/** `2026-10-22` → `22.10.2026`. */
const formatDate = iso => {
    const [y, m, d] = String(iso || '').split('-');
    return d && m && y ? `${d}.${m}.${y}` : iso;
};

/**
 * WHICH RELEASE IS RUNNING (22 Oct 2026: «щоб розуміти, яка версія UI, чи хто
 * користується оновив, якщо якісь питання») — at the foot of the menu: the UI
 * the browser holds and the API the server runs, both stamped from git (the
 * release tag, or the short commit). When they differ the line turns red: a UI
 * built from other sources than the API is how screens once showed raw
 * translation keys (9 Oct 2026). A development build has no stamp — «dev».
 */
const AppVersion = ({ compact }) => {
    const classes = useStyles();
    const [t] = useTranslation();
    const [api, setApi] = useState(undefined);

    useEffect(() => {
        let alive = true;
        axios(apiRequestOptions(apiPath.VERSION))
            .then(response => { if (alive) setApi((response.data && response.data.version) || null); })
            .catch(() => { if (alive) setApi(null); });

        return () => { alive = false; };
    }, []);

    const ui = UI_VERSION || 'dev';
    const mismatch = Boolean(UI_VERSION && api && UI_VERSION !== api);
    const full = [
        t('appVersion.ui', { version: ui, defaultValue: 'UI {{version}}' }) + (UI_BUILD_DATE ? ` · ${formatDate(UI_BUILD_DATE)}` : ''),
        t('appVersion.api', { version: api || '—', defaultValue: 'API {{version}}' }),
    ].join('\n');

    return (
        <Tooltip title={mismatch ? t('appVersion.mismatch', 'The interface and the server are different releases — rebuild the interface') : ''}>
            <Typography
                component="div"
                className={[classes.root, mismatch ? classes.mismatch : ''].filter(Boolean).join(' ')}
                data-testid="app-version"
                data-mismatch={mismatch ? 'true' : 'false'}
                style={{ whiteSpace: 'pre-line' }}
            >
                {compact ? ui : full}
            </Typography>
        </Tooltip>
    );
};

AppVersion.propTypes = {
    /** The collapsed rail — the UI release alone. */
    compact: PropTypes.bool,
};

AppVersion.defaultProps = {
    compact: false,
};

export default AppVersion;
